import { useEffect, useState } from 'react'
import axios from 'axios'
import { MIGRATE_MYTVS } from './useMyTvs'
import { useAuth } from '@praxis/component-auth'
import apiConfig from '../apiConfig'

/**
 * Set to 'mdm' when the PWA is distributed via enterprise MDM push.
 * Set to 'manual' to revert to the self-service browser install flow.
 */
export const DEPLOYMENT_MODE: 'mdm' | 'manual' = 'mdm'

/**
 * Set to a human-readable date string (e.g. "August 15, 2026") when a
 * legacy-app sunset date has been confirmed. Leave as null to suppress
 * the sunset warning.
 */
export const SUNSET_DATE: string | null = null

/**
 * Canonical production URLs. Users already on one of these URLs are on the
 * correct bookmark and should NOT see the "update your bookmark" notice.
 */
export const CANONICAL_URLS = [
  'https://trueyemobile.target.com',
  'https://trueyemobile.perf.target.com',
]

// Extend the Window interface to include mobile framework properties
declare global {
  interface Window {
    cordova?: any
    Cordova?: any
    Capacitor?: {
      isNativePlatform?: () => boolean
      platform?: string
    }
  }

  interface Navigator {
    standalone?: boolean
  }
}

export const ENV = {
  CORDOVA: 'cordova',
  CAPACITOR: 'capacitor',
  PWA: 'pwa_installed',
  WEB: 'web_browser',
  IN_APP_WEBVIEW: 'in_app_webview',
  UNKNOWN: 'unknown',
}

export const DB_ENV = {
  cordova: 'APP',
  capacitor: 'APP',
  pwa_installed: 'PWA',
  web_browser: 'BROWSER',
  in_app_webview: 'APP',
  unknown: 'UNKNOWN',
}

function isOnCanonicalUrl(): boolean {
  if (typeof window === 'undefined') return false
  const origin = window.location.origin
  return CANONICAL_URLS.some((url) => origin === url)
}

function isCordovaInAppBrowser(): boolean {
  const ua = navigator.userAgent || ''

  // Check if we're on a mobile device
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  if (!isMobile) {
    return false
  }

  // iOS Safari detection - these are normal browsers, NOT InAppBrowser
  const isIOSSafari =
    /iPhone|iPad|iPod/.test(ua) &&
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|OPiOS|mercury/.test(ua)

  // Check if it's a PWA (standalone mode)
  const isPWA =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  if (isPWA) {
    return false // PWAs are handled separately
  }

  // iOS detection: InAppBrowser has webkit.messageHandlers but is NOT Safari
  const hasWebkitHandlers = !!(window as any).webkit?.messageHandlers
  if (/iPhone|iPad|iPod/.test(ua) && hasWebkitHandlers && !isIOSSafari) {
    // Additional check: InAppBrowser typically lacks window.open functionality
    // or has limited window.open (returns null or restricted)
    try {
      const testWindow = window.open('', '_self')
      if (testWindow === null || testWindow === window) {
        return true // Likely InAppBrowser
      }
    } catch (e) {
      // If window.open throws or is restricted, likely InAppBrowser
      console.warn('Error detecting InAppBrowser:', e)
      return true
    }
  }

  // Android detection: Look for webview indicators
  // Android InAppBrowser uses a WebView which has 'wv' in the user agent
  const isAndroidWebView = ua.includes('Android') && ua.includes('wv')
  if (isAndroidWebView) {
    // Check if it lacks features that regular Chrome has
    const lacksServiceWorker = !('serviceWorker' in navigator)
    if (lacksServiceWorker) {
      return true
    }
  }

  // Additional heuristic: Mobile device that lacks Service Worker and isn't an old browser
  const lacksServiceWorker = !('serviceWorker' in navigator)
  const lacksNotifications = !('Notification' in window)
  const isModernUA =
    /Chrome\/[6-9]\d|Chrome\/[1-9]\d\d/.test(ua) || /Safari\/[6-9]\d/.test(ua)

  if (isMobile && lacksServiceWorker && lacksNotifications && isModernUA) {
    // Modern mobile browser that lacks these APIs = likely a restricted webview
    return true
  }

  return false
}

export default function useAppEnvironment({
  setCurrentMode,
}: {
  setCurrentMode?: Function
}) {
  const [env, setEnv] = useState(ENV.UNKNOWN)
  const auth = useAuth()

  // const makeToast = useToaster()

  useEffect(() => {
    try {
      // 1. Cordova with direct access to cordova object (main Cordova app)
      if (typeof window !== 'undefined' && (window.cordova || window.Cordova)) {
        setEnv(ENV.CORDOVA)
        return
      }

      // 1a. Cordova InAppBrowser (webview without cordova object)
      if (isCordovaInAppBrowser()) {
        setEnv(ENV.CORDOVA)
        return
      }

      // 1b. Capacitor
      if (typeof window !== 'undefined' && window.Capacitor) {
        // Capacitor has isNativePlatform or platform helpers
        const isNative = !!(
          window.Capacitor.isNativePlatform &&
          window.Capacitor.isNativePlatform()
        )
        if (isNative) {
          setEnv(ENV.CAPACITOR)
          return
        }
      }

      // 2. PWA installed (display-mode)
      const displayStandalone =
        window.matchMedia &&
        window.matchMedia('(display-mode: standalone)').matches
      const iosStandalone = window.navigator.standalone === true // iOS Safari
      if (displayStandalone || iosStandalone) {
        setEnv(ENV.PWA)
        return
      }

      // 3. Detect common in-app browsers / webviews
      const ua = navigator.userAgent || ''
      const isAndroidWV =
        ua.includes('wv') ||
        (ua.includes('Android') &&
          ua.includes('Version/') &&
          ua.includes('Chrome') === false)
      if (isAndroidWV) {
        setEnv(ENV.IN_APP_WEBVIEW)
        return
      }

      // 4. Regular browser tab
      setEnv(ENV.WEB)
    } catch (e) {
      console.warn('Error detecting environment:', e)
      setEnv(ENV.UNKNOWN)
    }
  }, [])

  function isInOldAppStoreApp() {
    return env === ENV.CORDOVA || env === ENV.CAPACITOR
  }

  function isInWebBrowser() {
    return env === ENV.WEB
  }

  function sendEnvAnalytics(env: string) {
    if (!apiConfig.analytics?.env) {
      console.warn(
        'Analytics endpoint not configured, skipping environment analytics',
      )
      return
    }
    axios
      .post(apiConfig.analytics?.env, {
        user_id: auth.session?.userInfo?.lanId,
        env: env,
      })
      .catch((e: unknown) => {
        console.warn('Failed to send environment analytics:', e)
      })
  }

  useEffect(() => {
    console.log('App environment detected:', env)
    if (
      setCurrentMode &&
      (isInOldAppStoreApp() || (isInWebBrowser() && !isOnCanonicalUrl()))
    ) {
      setCurrentMode(MIGRATE_MYTVS)
    }
    if (env !== ENV.UNKNOWN) {
      sendEnvAnalytics(DB_ENV[env as keyof typeof DB_ENV])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env])

  function isInPWA() {
    return env === ENV.PWA
  }

  return {
    env,
    enableEnvWorkzone:
      isInOldAppStoreApp() || (isInWebBrowser() && !isOnCanonicalUrl()),
    isInOldAppStoreApp: isInOldAppStoreApp(),
    isInWebBrowser: isInWebBrowser() && !isOnCanonicalUrl(),
    isInPWA: isInPWA(),
  }
}
